import { useGetByTenantAccountQuery } from 'api/rtk-api'
import { FC } from 'react'
import { formatCurrencyFull } from 'utilities/currency'

import {
  FieldSet,
  FormItem,
  Label,
  LabelValueRow,
  LegacyDatePicker,
  RadioInput,
  Section,
  Select,
  SingleColumnView,
  Space,
  TextInput,
  ValidationMessage
} from '../../../../../components/core-v2'
import { GenericValidationMessages } from '../../../../../components/core-v2/atoms/generic-validation-messages/index'
import { CURRENCY, TENANT } from '../../../../../const'
import { formatUnits } from '../../../../../utilities/number'
import { CollateralAlert } from '../../../util/Alerts'
import { WizardButtons } from '../../00-builder/wizard-buttons'
import { StyledSection } from '../../01-note/new-note/primitives'
import { FundingSource, TermsActions, TermsStepState } from '../state/terms-slice'
import { StyledTermsBlock } from './primitives'

export type TermsProps = {
  actions: TermsActions
  state: TermsStepState
}

export const TermsStateless: FC<TermsProps> = ({ actions, state }: TermsProps) => {
  const { data } = useGetByTenantAccountQuery({ tenant: TENANT })
  return (
    <SingleColumnView>
      <Section type="primary">
        <Space mb={6}>
          <StyledTermsBlock>
            <Space mx={5}>
              <Space mb={5}>
                <FormItem>
                  <Label>Trade name</Label>
                  <TextInput
                    data-testid="accountName"
                    isError={!!state.errors.accountName}
                    maxLength={64}
                    value={state.terms.accountName}
                    onChange={e => actions.setAccountName(e.target.value)}
                  />
                  <ValidationMessage errors={state.errors} fieldName="accountName" />
                </FormItem>
              </Space>
              <Space mb={5}>
                <FormItem>
                  <Label>Funding the trade</Label>
                  <StyledSection direction="vertical">
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={state.terms.fundingSource === FundingSource.Cash}
                        data-testid="cashFunded"
                        id="cashFunded"
                        type="radio"
                        onChange={() => actions.setFundingSource(FundingSource.Cash)}
                      />
                      <label htmlFor="cashFunded">Cash</label>
                    </FieldSet>
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={state.terms.fundingSource === FundingSource.Transfer}
                        id="transferFunded"
                        type="radio"
                        onChange={() => actions.setFundingSource(FundingSource.Transfer)}
                      />
                      <label htmlFor="transferFunded">Transfer units</label>
                    </FieldSet>
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={state.terms.fundingSource === FundingSource.Switch}
                        id="switchFromTrade"
                        type="radio"
                        onChange={() => actions.setFundingSource(FundingSource.Switch)}
                      />
                      <label htmlFor="switchFromTrade">Switch from another trade</label>
                    </FieldSet>
                  </StyledSection>
                  <ValidationMessage errors={state.errors} fieldName="fundingSource" />
                </FormItem>
              </Space>
              {state.terms.fundingSource === FundingSource.Cash && (
                <Space mb={5}>
                  <FormItem data-testid="investment-form-item">
                    <Label>
                      Investment amount (R{state.minimumInvestmentAmount.toLocaleString('en-GB')}{' '}
                      minimum)
                    </Label>
                    <TextInput
                      data-testid="investment"
                      isError={!!state.errors.investment}
                      value={formatUnits(Number(state?.terms?.investment) || 0)}
                      onChange={e =>
                        actions.setInvestment((e.target.value || '').replaceAll(',', ''))
                      }
                    />
                    <ValidationMessage errors={state.errors} fieldName="investment" />
                  </FormItem>
                </Space>
              )}
              {state.terms.fundingSource === FundingSource.Switch && (
                <>
                  <Space mb={5}>
                    <FormItem>
                      <Label>Funding the trade</Label>
                      <StyledSection direction="vertical">
                        <FieldSet direction="horizontal">
                          <RadioInput
                            checked={state.terms.fullSwitch}
                            data-testid="fullSwitch"
                            id="fullSwitch"
                            type="radio"
                            onChange={() => actions.setFullSwitch(true)}
                          />
                          <label htmlFor="fullSwitch">Full switch</label>
                        </FieldSet>
                        <FieldSet direction="horizontal">
                          <RadioInput
                            checked={!state.terms.fullSwitch}
                            data-testid="partialSwitch"
                            id="partialSwitch"
                            type="radio"
                            onChange={() => actions.setFullSwitch(false)}
                          />
                          <label htmlFor="partialSwitch">Partial switch</label>
                        </FieldSet>
                      </StyledSection>
                    </FormItem>
                  </Space>
                  <Space mb={5}>
                    <FormItem>
                      <Label>Choose an origination trade to switch from</Label>
                      <Select
                        data-testid="switchTrade"
                        isError={!!state.errors.switchTrade}
                        value={state.terms.switchTrade?.accountId || ''}
                        onChange={e => actions.setSwitchTrade(e.target.value)}
                      >
                        <option value="">Select a trade</option>
                        {state.terms.unlockedTrades
                          .filter(({ trade, unlocked }) => unlocked && trade.accountValue > 0)
                          .filter(({ trade }) => {
                            const account = data?.accounts.find(
                              ({ accountId }) => accountId === trade.accountId
                            )
                            return (
                              account &&
                              !(
                                account.currentlyApproving ||
                                account.currentlyClosing ||
                                account.currentlyUpdating
                              )
                            )
                          })
                          ?.map(({ trade }) => (
                            <option key={trade.accountName} value={trade.accountId}>
                              {trade.accountName}
                            </option>
                          ))}
                      </Select>
                      <ValidationMessage errors={state.errors} fieldName="switchTrade" />
                    </FormItem>
                  </Space>
                  {state.terms.switchTrade && (
                    <>
                      <Section data-testid="selected-trade" mb={6} pb={3} pt={3} type="secondary">
                        <LabelValueRow
                          label="Selected trade"
                          value={state.terms.switchTrade.accountName}
                        />
                        <LabelValueRow
                          label="Current value"
                          value={formatCurrencyFull(state.terms.switchTrade.accountValue, CURRENCY)}
                        />
                        <LabelValueRow
                          label="Tax classification"
                          value={
                            state.terms.switchTrade.capitalPercentage === 1 ? 'capital' : 'yield'
                          }
                        />
                      </Section>
                      {!state.terms.fullSwitch &&
                        state.minimumInvestmentAmount <=
                          state.terms.switchTrade.accountValue * 0.95 && (
                          <Space mb={5}>
                            <FormItem data-testid="switch-investment-form-item">
                              <Label>
                                Investment amount (minimum R
                                {state.minimumInvestmentAmount.toLocaleString('en-GB')} - maximum{' '}
                                {formatCurrencyFull(
                                  state.terms.switchTrade.accountValue * 0.95,
                                  CURRENCY
                                )}
                                )
                              </Label>
                              <TextInput
                                data-testid="switchInvestment"
                                isError={!!state.errors.switchInvestment}
                                value={formatUnits(Number(state?.terms?.switchInvestment) || 0)}
                                onChange={e =>
                                  actions.setSwitchInvestment(
                                    (e.target.value || '').replaceAll(',', '')
                                  )
                                }
                              />
                              <ValidationMessage
                                errors={state.errors}
                                fieldName="switchInvestment"
                              />
                            </FormItem>
                          </Space>
                        )}
                      {!state.terms.fullSwitch &&
                        state.minimumInvestmentAmount >
                          state.terms.switchTrade.accountValue * 0.95 && (
                          <Space mb={5}>
                            <ValidationMessage
                              errors={{
                                error:
                                  'Partial Switch is not available as value of Switch Out Trade is too low'
                              }}
                            />
                          </Space>
                        )}
                    </>
                  )}
                </>
              )}
              <Space mb={5}>
                <FormItem>
                  <Label>Start date</Label>
                  <StyledSection direction="vertical">
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={state.terms.requestedStartDate === ''}
                        data-testid="requestedStartDate_soonAsPossible"
                        id="soonAsPossible"
                        type="radio"
                        onChange={() => actions.setStartDate('')}
                      />
                      <label htmlFor="soonAsPossible">Start as soon as possible</label>
                    </FieldSet>
                    <FieldSet direction="horizontal">
                      <RadioInput
                        checked={state.terms.requestedStartDate !== ''}
                        data-testid="requestedStartDate_request"
                        id="request"
                        type="radio"
                        onChange={() => actions.setStartDate(state.nextWorkingDay)}
                      />
                      <label htmlFor="request">Request a start date</label>
                    </FieldSet>
                    {state.terms.requestedStartDate !== '' && (
                      <Space mt={4}>
                        <LegacyDatePicker
                          shouldAllowPublicHolidays={false}
                          testId="requestedStartDate"
                          value={new Date(state.terms.requestedStartDate)}
                          onChange={date => actions.setStartDate(date?.toString())}
                        />
                      </Space>
                    )}
                  </StyledSection>
                  <ValidationMessage errors={state.errors} fieldName="requestedStartDate" />
                </FormItem>
              </Space>
              <GenericValidationMessages errorMessages={state.generalErrors} />
            </Space>
          </StyledTermsBlock>
        </Space>
      </Section>
      <CollateralAlert errors={state.errors} />
      <WizardButtons
        canMoveBack
        canMoveNext={state.canProceed}
        onMoveBack={() => actions.moveBack()}
        onMoveNext={() => actions.requestMoveNext()}
      />
    </SingleColumnView>
  )
}
